import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useBackToList } from '../../hooks/useBackToList';
import axios from '../../Api';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faSave, faTimes, faPlus, faEdit, faTrash, faClone,
  faChevronLeft, faChevronRight, faFilter, faInfoCircle,
  faExclamationTriangle, faCheckCircle, faBars, faSync, faLock, faFlagCheckered,
  faSpinner, faFileExcel, faUsers
} from '@fortawesome/free-solid-svg-icons';
import { useAuth } from '../../contexts/AuthContext';
import { QuotaCalculator } from '../../services/QuotaCalculator';
import DimensionBuilder from './components/DimensionBuilder';
import QuotaMatrix from './components/QuotaMatrix';
import QuotaSummary from './components/QuotaSummary';
import ContextChips from './components/ContextChips';
import CellDetailPanel from './components/CellDetailPanel';
import QuotaHelperPanel from './components/QuotaHelperPanel';
import AlertModal from './components/AlertModal';
import ConfirmationModal from './components/ConfirmationModal';
import RejectionModal from './components/RejectionModal';
import AssignSupervisorModal from './components/AssignSupervisorModal';
import './QuotaBuilder.css';

const QuotaBuilder = () => {
  const { projectId } = useParams();
  const navigate = useNavigate();
  // Retour vers la liste en conservant recherche/filtre/page (ils vivent dans l'URL).
  const backToList = useBackToList('/quotas');
  const { user } = useAuth();
  
  // Main state
  const [project, setProject] = useState(null);
  const [survey, setSurvey] = useState(null);
  const [dimensions, setDimensions] = useState([]);
  const [cells, setCells] = useState([]);
  const [pendingAssignments, setPendingAssignments] = useState([]);
  const [pendingModifications, setPendingModifications] = useState([]);
  const [pendingAddresses, setPendingAddresses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [updatingCells, setUpdatingCells] = useState(false);
  
  // UI state
  const [selectedCell, setSelectedCell] = useState(null);
  const [showCellDetail, setShowCellDetail] = useState(false);
  const [contextFilters, setContextFilters] = useState({});
  const [alertModal, setAlertModal] = useState({ show: false, title: '', message: '', type: 'info' });
  const [showDimensionBuilder, setShowDimensionBuilder] = useState(true);
  const [showSummary, setShowSummary] = useState(true);
  const [validating, setValidating] = useState(false);
  const [showValidateConfirm, setShowValidateConfirm] = useState(false);
  const [finishing, setFinishing] = useState(false);
  const [showFinishConfirm, setShowFinishConfirm] = useState(false);
  const [submittingForApproval, setSubmittingForApproval] = useState(false);
  const [approving, setApproving] = useState(false);
  const [rejecting, setRejecting] = useState(false);
  const [showApproveConfirm, setShowApproveConfirm] = useState(false);
  const [showRejectionModal, setShowRejectionModal] = useState(false);
  const [unlocking, setUnlocking] = useState(false);
  const [showUnlockConfirm, setShowUnlockConfirm] = useState(false);
  const [showBulkAssignModal, setShowBulkAssignModal] = useState(false);
  
  // Mobile sidebar state
  const [mobileSidebarOpen, setMobileSidebarOpen] = useState(null); // 'dimensions' | 'summary' | null
  
  // Calculator instance
  const [calculator, setCalculator] = useState(null);

  // Check workflow permissions (after state declarations)
  // Assignment workflow
  const canSubmitForApproval = user && user.role === 'chef_terrain' &&
    project && !['locked', 'finished', 'archived'].includes(project.status) &&
    project.assignment_status !== 'pending_approval' &&
    (pendingAssignments.length > 0 || pendingModifications.length > 0 || pendingAddresses.length > 0);
  const canApprove = user && user.role === 'chef_terrain_superieur' && 
    project && !['locked', 'finished', 'archived'].includes(project.status) &&
    project.assignment_status === 'pending_approval';
  const canReject = user && user.role === 'chef_terrain_superieur' && 
    project && !['locked', 'finished', 'archived'].includes(project.status) &&
    project.assignment_status === 'pending_approval';
  
  // Breakdown of everything waiting in the current approval batch, shown to the
  // chef_terrain_superieur before he validates (addresses included — a batch can be
  // address-only when the CT just deleted assigned addresses).
  const addressChanges = {
    created: pendingAddresses.filter(pa => pa.operation === 'create'),
    updated: pendingAddresses.filter(pa => pa.operation === 'update'),
    deleted: pendingAddresses.filter(pa => pa.operation === 'delete'),
  };
  const cellChanges = {
    created: pendingModifications.filter(pm => pm.field === 'create'),
    deleted: pendingModifications.filter(pm => pm.field === 'delete'),
    targets: pendingModifications.filter(pm => pm.field === 'target_count'),
    others: pendingModifications.filter(pm => !['create', 'delete', 'target_count'].includes(pm.field)),
  };
  const totalPendingChanges = pendingAssignments.length + pendingModifications.length + pendingAddresses.length;

  const addressLabel = (pa) => {
    const who = pa.supervisor?.name ? ` — ${pa.supervisor.name}` : '';
    const code = pa.agency_code ? ` (${pa.agency_code})` : '';
    return `${pa.ref ? pa.ref + ' · ' : ''}${pa.name || pa.address_text || 'Adresse'}${code}${who}`;
  };

  // Unlock permission - chef_terrain_superieur and chef_terrain when locked
  const canUnlock = user && (user.role === 'chef_terrain_superieur' || user.role === 'chef_terrain') &&
    project && project.status === 'locked';
  
  // Debug: Log unlock permission (remove in production)
  useEffect(() => {
    if (project) {
      console.log('Unlock Debug:', {
        userRole: user?.role,
        projectStatus: project?.status,
        canUnlock: canUnlock
      });
    }
  }, [project, user, canUnlock]);
  
  // Legacy permissions (for backward compatibility)
  const canValidate = user && (user.role === 'chef_terrain' || user.role === 'chef_terrain_superieur');
  const canFinish = user && (user.role === 'charge_etude' || user.role === 'charge_etude_superieur' || user.role === 'chef_terrain' || user.role === 'chef_terrain_superieur');
  
  // Check if project is in read-only mode
  const isReadOnly = ['administrateur_general', 'administrateur_general_superieur'].includes(user?.role);
  
  // Check assignment permission based on role
  // chef_terrain can always assign (no delegation needed), blocked only by pending_approval or locked/finished/archived
  const projectLockedFinishedOrArchived = project?.status && ['locked', 'finished', 'archived'].includes(project.status);
  const canAssignSupervisor = user &&
    project?.assignment_status !== 'pending_approval' &&
    !projectLockedFinishedOrArchived && (
      (user.role === 'chef_terrain_superieur') ||
      (user.role === 'chef_terrain')
    );
  
  // Bulk assign button visibility - same permissions as single assign, but not for charge_etude roles
  const canBulkAssignSupervisors = canAssignSupervisor && 
    !['charge_etude', 'charge_etude_superieur'].includes(user?.role) &&
    cells.length > 0;
  
  // Debug log (remove in production)
  useEffect(() => {
    if (user && project) {
      console.log('QuotaBuilder - User role:', user.role, 'isReadOnly:', isReadOnly, 'Project status:', project?.status);
      console.log('Bulk Assign Debug:', {
        canAssignSupervisor,
        userRole: user?.role,
        projectStatus: project?.status,
        assignmentStatus: project?.assignment_status,
        projectLockedFinishedOrArchived,
        cellsCount: cells.length,
        canBulkAssignSupervisors
      });
    }
  }, [user, isReadOnly, project?.status, project?.assignment_status, canAssignSupervisor, canBulkAssignSupervisors, cells.length, projectLockedFinishedOrArchived]);

  useEffect(() => {
    if (projectId) {
      fetchProjectData();
    } else {
      setLoading(false);
    }
  }, [projectId]);

  useEffect(() => {
    if (project && dimensions && cells) {
      setCalculator(new QuotaCalculator(project, dimensions, cells));
    }
  }, [project, dimensions, cells]);

  // Auto-collapse sections when project is locked/finished/archived
  // or when chef_terrain_superieur is reviewing pending approval
  useEffect(() => {
    if (project?.status && ['locked', 'finished', 'archived'].includes(project.status)) {
      setShowDimensionBuilder(false);
      setShowSummary(false);
    }
    if (user?.role === 'chef_terrain_superieur' && project?.assignment_status === 'pending_approval') {
      setShowDimensionBuilder(false);
      setShowSummary(false);
    }
  }, [project?.status, project?.assignment_status, user?.role]);

  const fetchProjectData = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`/quotas/projects/${projectId}`);
      const data = response.data.data;
      
      setProject(data);
      setSurvey(data.survey);
      setDimensions(data.dimensions || []);
      setCells(data.cells || []);
      // Normalize pending assignments IDs to numbers for consistent comparison
      const normalizedPendingAssignments = (data.pending_assignments || []).map(pa => ({
        ...pa,
        quota_cell_id: Number(pa.quota_cell_id),
        quota_project_id: Number(pa.quota_project_id),
        supervisor_id: Number(pa.supervisor_id),
        previous_supervisor_id: pa.previous_supervisor_id ? Number(pa.previous_supervisor_id) : null,
        assigned_by_user_id: pa.assigned_by_user_id ? Number(pa.assigned_by_user_id) : null,
      }));
      setPendingAssignments(normalizedPendingAssignments);
      // Normalize pending modifications
      const normalizedPendingModifications = (data.pending_modifications || []).map(pm => ({
        ...pm,
        quota_cell_id: Number(pm.quota_cell_id),
        quota_project_id: Number(pm.quota_project_id),
      }));
      setPendingModifications(normalizedPendingModifications);

      setPendingAddresses((data.pending_addresses || []).map(pa => ({
        ...pa,
        quota_project_id: Number(pa.quota_project_id),
      })));
    } catch (error) {
      console.error('Error fetching project:', error);
      let errorMessage = 'Erreur lors du chargement du projet de quota';
      
      if (error.response?.status === 429) {
        errorMessage = 'Trop de requêtes. Veuillez patienter quelques instants avant de réessayer.';
      } else if (error.response?.status === 404) {
        errorMessage = 'Projet de quota non trouvé. Il a peut-être été supprimé.';
      } else if (error.response?.data?.message) {
        errorMessage = error.response.data.message;
      }
      
      setAlertModal({
        show: true,
        title: 'Erreur',
        message: errorMessage,
        type: 'error'
      });
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    if (!project) return;
    
    try {
      setSaving(true);
      await axios.put(`/quotas/projects/${projectId}`, {
        status: 'in_progress'
      });
      setAlertModal({
        show: true,
        title: 'Succès',
        message: 'Le projet a été enregistré avec succès',
        type: 'success'
      });
      await fetchProjectData();
    } catch (error) {
      console.error('Error updating project status:', error);
      setAlertModal({
        show: true,
        title: 'Erreur',
        message: error.response?.data?.message || 'Erreur lors de la mise à jour du statut',
        type: 'error'
      });
    } finally {
      setSaving(false);
    }
  };

  const handleValidate = () => {
    setShowValidateConfirm(true);
  };

  const confirmValidate = async () => {
    if (!project) return;
    
    try {
      setValidating(true);
      await axios.put(`/quotas/projects/${projectId}`, {
        status: 'locked'
      });
      
      setAlertModal({
        show: true,
        title: 'Succès',
        message: 'Le projet a été démarré et verrouillé avec succès. Il n\'est plus modifiable.',
        type: 'success'
      });
      
      await fetchProjectData();
    } catch (error) {
      console.error('Error validating project:', error);
      setAlertModal({
        show: true,
        title: 'Erreur',
        message: error.response?.data?.message || 'Erreur lors du verrouillage du quota',
        type: 'error'
      });
    } finally {
      setValidating(false);
      setShowValidateConfirm(false); // close the confirm modal so the alert is visible
    }
  };

  const handleFinish = () => {
    setShowFinishConfirm(true);
  };

  const confirmFinish = async () => {
    if (!project) return;
    
    try {
      setFinishing(true);
      await axios.put(`/quotas/projects/${projectId}`, {
        status: 'finished'
      });
      
      setAlertModal({
        show: true,
        title: 'Succès',
        message: 'Le quota a été marqué comme terminé.',
        type: 'success'
      });
      
      await fetchProjectData();
    } catch (error) {
      console.error('Error finishing project:', error);
      setAlertModal({
        show: true,
        title: 'Erreur',
        message: error.response?.data?.message || 'Erreur lors de la finalisation du quota',
        type: 'error'
      });
    } finally {
      setFinishing(false);
      setShowFinishConfirm(false); // close the confirm modal so the alert is visible
    }
  };

  // Assignment permission handlers
  const handleSubmitForApproval = async () => {
    if (!project) return;
    
    try {
      setSubmittingForApproval(true);
      await axios.post(`/quotas/projects/${projectId}/submit-for-approval`);
      
      setAlertModal({
        show: true,
        title: 'Succès',
        message: 'Les assignations ont été soumises pour approbation avec succès.',
        type: 'success'
      });
      
      await fetchProjectData();
    } catch (error) {
      console.error('Error submitting for approval:', error);
      setAlertModal({
        show: true,
        title: 'Erreur',
        message: error.response?.data?.message || 'Erreur lors de la soumission',
        type: 'error'
      });
    } finally {
      setSubmittingForApproval(false);
    }
  };

  // Show the summary of what's about to be approved before actually approving.
  const handleApprove = () => {
    if (!project) return;
    setShowApproveConfirm(true);
  };

  const confirmApprove = async () => {
    if (!project) return;

    try {
      setApproving(true);
      const response = await axios.post(`/quotas/projects/${projectId}/approve-assignments`);
      setShowApproveConfirm(false);

      setAlertModal({
        show: true,
        title: 'Succès',
        message: `${response.data?.message || 'Modifications approuvées avec succès.'} Les prestataires peuvent maintenant voir leurs quotas.`,
        type: 'success'
      });

      await fetchProjectData();
    } catch (error) {
      console.error('Error approving assignments:', error);
      setShowApproveConfirm(false);
      setAlertModal({
        show: true,
        title: 'Erreur',
        message: error.response?.data?.message || 'Erreur lors de l\'approbation',
        type: 'error'
      });
    } finally {
      setApproving(false);
    }
  };

  const handleReject = () => {
    setShowRejectionModal(true);
  };

  const handleUnlock = () => {
    setShowUnlockConfirm(true);
  };

  const confirmUnlock = async () => {
    if (!project) return;
    
    try {
      setUnlocking(true);
      await axios.post(`/quotas/projects/${projectId}/unlock`);
      
      setAlertModal({
        show: true,
        title: 'Succès',
        message: 'Le projet a été déverrouillé avec succès. Les quotas ne sont plus visibles pour les prestataires et enquêteurs.',
        type: 'success'
      });
      
      await fetchProjectData();
    } catch (error) {
      console.error('Error unlocking project:', error);
      setAlertModal({
        show: true,
        title: 'Erreur',
        message: error.response?.data?.message || 'Erreur lors du déverrouillage du projet',
        type: 'error'
      });
    } finally {
      setUnlocking(false);
      setShowUnlockConfirm(false); // close the confirm modal so the alert is visible
    }
  };

  const confirmReject = async (remarks) => {
    if (!project) return;
    
    try {
      setRejecting(true);
      const response = await axios.post(`/quotas/projects/${projectId}/reject-assignments`, {
        rejection_remarks: remarks
      });
      
      // Close modal immediately on success
      setShowRejectionModal(false);
      
      // Show success message
      setAlertModal({
        show: true,
        title: 'Succès',
        message: 'Les assignations ont été rejetées. Le chef de terrain peut les corriger.',
        type: 'success'
      });
      
      // Refresh project data silently (without showing errors)
      try {
        await fetchProjectData();
      } catch (refreshError) {
        // Silently handle refresh errors - rejection already succeeded
        console.error('Error refreshing data after rejection:', refreshError);
      }
    } catch (error) {
      console.error('Error rejecting assignments:', error);
      setAlertModal({
        show: true,
        title: 'Erreur',
        message: error.response?.data?.message || 'Erreur lors du rejet',
        type: 'error'
      });
    } finally {
      setRejecting(false);
    }
  };

  const handleUpdateCells = async () => {
    if (!projectId) return;
    
    try {
      setUpdatingCells(true);
      const response = await axios.post(`/quotas/projects/${projectId}/update-cells`);
      
      if (response.data.success) {
        setAlertModal({
          show: true,
          title: 'Succès',
          message: response.data.message || 'Mise à jour des cellules lancée avec succès.',
          type: 'success'
        });
        
        setTimeout(() => {
          fetchProjectData();
        }, 3000);
      }
    } catch (error) {
      console.error('Error updating cells:', error);
      setAlertModal({
        show: true,
        title: 'Erreur',
        message: error.response?.data?.message || 'Erreur lors de la mise à jour des cellules',
        type: 'error'
      });
    } finally {
      setUpdatingCells(false);
    }
  };

  const handleDimensionAdded = (newDimension) => {
    setDimensions([...dimensions, newDimension]);
  };

  const handleDimensionUpdated = (updatedDimension) => {
    setDimensions(dimensions.map(d => 
      d.id === updatedDimension.id ? updatedDimension : d
    ));
  };

  const handleDimensionDeleted = (dimensionId) => {
    setDimensions(dimensions.filter(d => d.id !== dimensionId));
    setCells(cells.filter(c => {
      const cellValues = c.dimension_values_json || {};
      return !cellValues.hasOwnProperty(dimensionId.toString());
    }));
  };

  const handleCellAdded = (newCell) => {
    if (Array.isArray(newCell)) {
      setCells(prevCells => [...prevCells, ...newCell]);
    } else {
      setCells(prevCells => [...prevCells, newCell]);

      // If chef_terrain created this cell, add pending 'create' modification
      if (newCell._pendingCreation) {
        setPendingModifications(prev => [
          ...prev,
          {
            quota_cell_id: Number(newCell.id),
            quota_project_id: Number(projectId),
            field: 'create',
          }
        ]);
      }
    }
  };

  const handleCellUpdated = (updatedCell) => {
    // If this was a pending assignment creation (chef_terrain), update pendingAssignments state first
    // Handle multiple pending assignments (new system)
    if (updatedCell._pendingAssignments && Array.isArray(updatedCell._pendingAssignments)) {
      setPendingAssignments(prev => {
        // Normalize cell ID to number for consistent comparison
        const cellId = Number(updatedCell.id);
        
        // Remove all existing pending assignments for this cell
        // Compare as numbers to handle string/number mismatch from backend
        const filtered = prev.filter(pa => Number(pa.quota_cell_id) !== cellId);
        
        // Add new pending assignments - ensure they have the correct cell ID as a number
        const newAssignments = updatedCell._pendingAssignments.map(pa => ({
          ...pa,
          quota_cell_id: Number(pa.quota_cell_id || updatedCell.id),
          quota_project_id: Number(pa.quota_project_id || projectId),
          supervisor_id: Number(pa.supervisor_id),
          previous_supervisor_id: pa.previous_supervisor_id ? Number(pa.previous_supervisor_id) : null,
          assigned_by_user_id: pa.assigned_by_user_id ? Number(pa.assigned_by_user_id) : null,
        }));
        
        // Create a new array reference to ensure React detects the change
        return [...filtered, ...newAssignments];
      });
    }
    
    // Handle pending modifications (chef_terrain cell updates)
    const cellId = Number(updatedCell.id);
    const projId = Number(projectId);

    if (updatedCell.pending_target_count !== undefined) {
      setPendingModifications(prev => {
        const filtered = prev.filter(pm => !(Number(pm.quota_cell_id) === cellId && pm.field === 'target_count'));
        filtered.push({
          quota_cell_id: cellId,
          quota_project_id: projId,
          field: 'target_count',
          old_value: updatedCell.target_count,
          new_value: updatedCell.pending_target_count,
        });
        return filtered;
      });
    }

    if (updatedCell.pending_notes !== undefined) {
      setPendingModifications(prev => {
        const filtered = prev.filter(pm => !(Number(pm.quota_cell_id) === cellId && pm.field === 'notes'));
        filtered.push({
          quota_cell_id: cellId,
          quota_project_id: projId,
          field: 'notes',
          new_value_text: updatedCell.pending_notes,
        });
        return filtered;
      });
    }

    if (updatedCell.pending_dimension_values_json !== undefined) {
      setPendingModifications(prev => {
        const filtered = prev.filter(pm => !(Number(pm.quota_cell_id) === cellId && pm.field === 'dimension_values_json'));
        filtered.push({
          quota_cell_id: cellId,
          quota_project_id: projId,
          field: 'dimension_values_json',
          new_value_text: JSON.stringify(updatedCell.pending_dimension_values_json),
        });
        return filtered;
      });
    }

    // Handle pending deletion (chef_terrain cell delete)
    if (updatedCell._pendingDeletion) {
      setPendingModifications(prev => {
        // Remove ALL other pending mods for this cell and add only 'delete'
        const filtered = prev.filter(pm => Number(pm.quota_cell_id) !== cellId);
        filtered.push({
          quota_cell_id: cellId,
          quota_project_id: projId,
          field: 'delete',
        });
        return filtered;
      });
      return; // Don't update cell data — cell stays as-is in the list
    }

    // Update cells array - ensure assigned_supervisors is preserved
    setCells(prevCells => prevCells.map(c => {
      if (c.id === updatedCell.id) {
        // Merge to ensure assigned_supervisors array is preserved
        return {
          ...updatedCell,
          assigned_supervisors: updatedCell.assigned_supervisors || c.assigned_supervisors || []
        };
      }
      return c;
    }));

    // Update selected cell if it's the one being updated
    if (selectedCell && selectedCell.id === updatedCell.id) {
      setSelectedCell(updatedCell);
    }
  };

  const handleCellDeleted = (cellId) => {
    setCells(prevCells => prevCells.filter(c => c.id !== cellId));
    // Also remove any pending modifications for this cell
    setPendingModifications(prev => prev.filter(pm => Number(pm.quota_cell_id) !== Number(cellId)));
    if (selectedCell && selectedCell.id === cellId) {
      setSelectedCell(null);
      setShowCellDetail(false);
    }
  };

  const handleCancelPendingDeletion = async (cellId) => {
    try {
      await axios.delete(`/quotas/projects/${projectId}/cells/${cellId}/pending-deletion`);
      setPendingModifications(prev =>
        prev.filter(pm => !(Number(pm.quota_cell_id) === Number(cellId) && pm.field === 'delete'))
      );
    } catch (error) {
      console.error('Error cancelling pending deletion:', error);
      setAlertModal({
        show: true,
        title: 'Erreur',
        message: error.response?.data?.message || 'Erreur lors de l\'annulation',
        type: 'error'
      });
    }
  };

  const handleContextFilterChange = (dimensionId, valueCode) => {
    setContextFilters(prev => {
      if (valueCode === null) {
        const newFilters = { ...prev };
        delete newFilters[dimensionId];
        return newFilters;
      }
      return { ...prev, [dimensionId]: valueCode };
    });
  };

  const clearContextFilters = () => {
    setContextFilters({});
  };

  // Export to Excel
  const [exporting, setExporting] = useState(false);
  
  const handleExportExcel = async () => {
    if (!cells || cells.length === 0) {
      setAlertModal({
        show: true,
        title: 'Export impossible',
        message: 'Aucune cellule à exporter',
        type: 'warning'
      });
      return;
    }

    try {
      setExporting(true);
      const response = await axios.get(`/quotas/projects/${projectId}/export`, {
        responseType: 'blob',
      });

      // Get filename from response headers or use default
      const contentDisposition = response.headers['content-disposition'];
      let filename = `quota_${survey?.title || projectId}_${new Date().toISOString().split('T')[0]}.xlsx`;
      if (contentDisposition) {
        const filenameMatch = contentDisposition.match(/filename="?(.+)"?/i);
        if (filenameMatch) {
          filename = filenameMatch[1];
        }
      }

      // Create blob and download
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error exporting:', error);
      setAlertModal({
        show: true,
        title: 'Erreur',
        message: 'Erreur lors de l\'exportation. Veuillez réessayer.',
        type: 'error'
      });
    } finally {
      setExporting(false);
    }
  };

  // Status badge styling
  const getStatusBadge = (status) => {
    const styles = {
      draft: 'bg-yellow-100 text-yellow-800',
      in_progress: 'bg-blue-100 text-blue-800',
      review: 'bg-purple-100 text-purple-800',
      locked: 'bg-red-100 text-red-800',
      finished: 'bg-green-100 text-green-800',
      archived: 'bg-gray-100 text-gray-800'
    };
    const labels = {
      draft: 'Brouillon',
      in_progress: 'En cours',
      review: 'En révision',
      locked: 'Verrouillé',
      finished: 'Terminé',
      archived: 'Archivé'
    };
    return { style: styles[status] || styles.draft, label: labels[status] || status };
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex flex-col items-center justify-center gap-4">
        <FontAwesomeIcon icon={faSpinner} className="text-4xl text-red-600 animate-spin" />
        <p className="text-gray-600">Chargement du projet de quota...</p>
      </div>
    );
  }

  if (!project) {
    return (
      <div className="min-h-screen bg-gray-50 flex flex-col items-center justify-center gap-4">
        <FontAwesomeIcon icon={faExclamationTriangle} className="text-4xl text-red-600" />
        <p className="text-gray-600">Projet de quota non trouvé</p>
        <button 
          onClick={backToList}
          className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
        >
          Retour à la liste
        </button>
      </div>
    );
  }

  const summary = calculator?.getOverallSummary() || {};
  const warnings = calculator?.getValidationWarnings() || [];
  const statusBadge = getStatusBadge(project.status);

  return (
    <div className="h-screen bg-gray-50 flex flex-col overflow-hidden">
      {/* Top Bar */}
      <div className="flex-shrink-0 bg-white border-b border-gray-200 shadow-sm z-30">
        <div className="px-4 py-3">
          {/* Mobile: Stack vertically */}
          <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
            {/* Left side: Title */}
            <div className="flex items-center gap-3 min-w-0">
              <div className="min-w-0 flex-1">
                <h1 className="text-lg lg:text-xl font-semibold text-gray-900 truncate">
                  {survey?.title || 'Projet de Quota'}
                </h1>
                {/* Vague (wave) badge for tracker surveys */}
                {project?.survey_wave?.label && (
                  <span className="inline-block mt-1 px-2 py-0.5 text-xs font-medium rounded-full bg-blue-100 text-blue-700">
                    {project.survey_wave.label}
                  </span>
                )}
                {/* Fieldwork Dates — for a vague (wave) quota, use the wave's own
                    window so the dates always match the "Vague X" label above. */}
                {(() => {
                  const fwStart = project?.survey_wave?.date_debut || project?.fieldwork_start_date;
                  const fwEnd = project?.survey_wave?.date_fin || project?.fieldwork_end_date;
                  if (!fwStart && !fwEnd) return null;
                  return (
                    <div className="flex items-center gap-3 mt-1 text-xs text-gray-600 flex-wrap">
                      {fwStart && (
                        <span>
                          <strong>Début:</strong> {new Date(fwStart).toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit', year: 'numeric' })}
                        </span>
                      )}
                      {fwStart && fwEnd && (
                        <span className="text-gray-400">•</span>
                      )}
                      {fwEnd && (
                        <span>
                          <strong>Fin:</strong> {new Date(fwEnd).toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit', year: 'numeric' })}
                        </span>
                      )}
                    </div>
                  );
                })()}
                <div className="flex items-center gap-2 mt-1 flex-wrap">
                  <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${statusBadge.style}`}>
                    {statusBadge.label}
                  </span>
                  {warnings.length > 0 && (
                    <span className="px-2 py-0.5 bg-orange-100 text-orange-700 rounded-full text-xs font-medium flex items-center gap-1">
                      <FontAwesomeIcon icon={faExclamationTriangle} className="text-[10px]" />
                      {warnings.length} alerte(s)
                    </span>
                  )}
                </div>
              </div>
            </div>

            {/* Right side: Buttons */}
            <div className="flex items-center gap-3 flex-wrap">
              {/* Left side buttons */}
              <div className="flex items-center gap-3 flex-wrap">
                {/* Refresh Button - Only for non-chef_terrain or when not read-only */}
                {!isReadOnly && (
                  <button 
                    onClick={handleUpdateCells}
                    disabled={updatingCells || !projectId}
                    className="px-3 py-2 bg-emerald-600 hover:bg-emerald-700 disabled:bg-emerald-400 text-white rounded-lg flex items-center gap-2 text-sm font-medium transition-colors"
                    title="Actualiser les compteurs"
                  >
                    <FontAwesomeIcon icon={faSync} className={updatingCells ? 'animate-spin' : ''} />
                    <span className="hidden sm:inline">{updatingCells ? 'Mise à jour...' : 'Actualiser'}</span>
                  </button>
                )}

                {/* Export Button - Always available (read-only operation) */}
                <button 
                  onClick={handleExportExcel}
                  disabled={exporting || !cells || cells.length === 0}
                  className="px-3 py-2 bg-red-600 hover:bg-red-700 disabled:bg-red-400 text-white rounded-lg flex items-center gap-2 text-sm font-medium transition-colors"
                  title="Exporter en Excel"
                >
                  <FontAwesomeIcon icon={exporting ? faSpinner : faFileExcel} className={exporting ? 'animate-spin' : ''} />
                  <span className="hidden sm:inline">{exporting ? 'Export...' : 'Exporter'}</span>
                </button>

                {/* Running shortfall against the project target. The two totals must
                    BOTH equal the target before the batch can be closed, so the number
                    that blocks the submit is shown next to it rather than discovered on
                    click. Computed server-side (strict_totals) so it can't drift. */}
                {canSubmitForApproval && project?.strict_totals && !project.strict_totals.can_close && (
                  <div
                    className="px-3 py-2 bg-amber-50 border border-amber-200 text-amber-800 rounded-lg flex items-center gap-2 text-sm"
                    title={`Objectif du projet : ${project.strict_totals.target}`}
                  >
                    <FontAwesomeIcon icon={faExclamationTriangle} />
                    <span>
                      {project.strict_totals.allocation_remaining !== 0 ? (
                        <>
                          Cellules : {project.strict_totals.allocated} / {project.strict_totals.target}
                          {' — '}
                          {project.strict_totals.allocation_remaining > 0
                            ? `${project.strict_totals.allocation_remaining} à répartir`
                            : `${Math.abs(project.strict_totals.allocation_remaining)} de trop`}
                        </>
                      ) : (
                        <>
                          {project.strict_totals.assignment_remaining > 0
                            ? `Reste à assigner : ${project.strict_totals.assignment_remaining}`
                            : `${Math.abs(project.strict_totals.assignment_remaining)} assigné(s) de trop`}
                          {' '}({project.strict_totals.assigned} / {project.strict_totals.target})
                        </>
                      )}
                    </span>
                  </div>
                )}

                {/* Submit for Approval Button (chef_terrain only) */}
                {canSubmitForApproval && (
                  <button 
                    onClick={handleSubmitForApproval}
                    disabled={submittingForApproval}
                    className="px-3 py-2 bg-green-600 hover:bg-green-700 disabled:bg-green-400 text-white rounded-lg flex items-center gap-2 text-sm font-medium transition-colors shadow-sm"
                    title="Soumettre pour approbation"
                  >
                    <FontAwesomeIcon icon={submittingForApproval ? faSpinner : faCheckCircle} className={submittingForApproval ? 'animate-spin' : ''} />
                    <span className="hidden sm:inline">
                      {submittingForApproval ? 'Soumission...' : 'Soumettre pour approbation'}
                      {!submittingForApproval && totalPendingChanges > 0 && (
                        <span className="ml-1 px-1.5 py-0.5 bg-green-800 text-white rounded-full text-xs">
                          {totalPendingChanges}
                        </span>
                      )}
                    </span>
                  </button>
                )}

                {/* Start/Lock Project Button - chef_terrain_superieur always, chef_terrain only after approval with no pending changes */}
                {(user?.role === 'chef_terrain_superieur' || (user?.role === 'chef_terrain' && project?.assignment_status === 'approved' && pendingModifications.length === 0 && pendingAssignments.length === 0)) && project?.status !== 'locked' && project?.status !== 'finished' && !isReadOnly && (
                  <button
                    onClick={handleValidate}
                    disabled={validating}
                    className="px-3 py-2 bg-amber-500 hover:bg-amber-600 disabled:bg-amber-300 text-white rounded-lg flex items-center gap-2 text-sm font-medium transition-colors"
                    title="Démarrer le projet et verrouiller"
                  >
                    <FontAwesomeIcon icon={faFlagCheckered} />
                    <span className="hidden sm:inline">{validating ? 'Démarrage...' : 'Démarrer le projet'}</span>
                  </button>
                )}

                {/* Unlock Button (Modifier) */}
                {canUnlock && (
                  <button 
                    onClick={handleUnlock}
                    disabled={unlocking}
                    className="px-3 py-2 bg-orange-600 hover:bg-orange-700 disabled:bg-orange-400 text-white rounded-lg flex items-center gap-2 text-sm font-medium transition-colors"
                    title="Déverrouiller le projet pour modification"
                  >
                    <FontAwesomeIcon icon={unlocking ? faSpinner : faEdit} className={unlocking ? 'animate-spin' : ''} />
                    <span className="hidden sm:inline">{unlocking ? 'Déverrouillage...' : 'Modifier'}</span>
                  </button>
                )}

                {/* Finish Button */}
                {canFinish && project?.status === 'locked' && (
                  <button 
                    onClick={handleFinish}
                    disabled={finishing}
                    className="px-3 py-2 bg-green-600 hover:bg-green-700 disabled:bg-green-400 text-white rounded-lg flex items-center gap-2 text-sm font-medium transition-colors"
                    title="Marquer comme terminé"
                  >
                    <FontAwesomeIcon icon={faFlagCheckered} />
                    <span className="hidden sm:inline">{finishing ? 'Finalisation...' : 'Terminer'}</span>
                  </button>
                )}

                {/* Start Project Button (only shows when status is draft) */}
                {project?.status === 'draft' && !isReadOnly && (
                  <button 
                    onClick={handleSave}
                    disabled={saving}
                    className="px-3 py-2 bg-blue-600 hover:bg-blue-700 disabled:bg-blue-400 text-white rounded-lg flex items-center gap-2 text-sm font-medium transition-colors shadow-sm"
                    title="Enregistrer le projet"
                  >
                    <FontAwesomeIcon icon={saving ? faSpinner : faSave} className={saving ? 'animate-spin' : ''} />
                    <span className="hidden sm:inline">{saving ? 'Enregistrement...' : 'Enregistrer'}</span>
                  </button>
                )}
              </div>

              {/* Approve/Reject Assignment Group - Right side */}
              {(canApprove || canReject) && (
                <div className="flex items-center gap-2 px-3 py-2 border-2 border-gray-300 rounded-lg bg-gradient-to-r from-blue-50 to-indigo-50 shadow-sm ml-auto">
                  {/* Pending counts summary */}
                  <div className="flex items-center gap-1 text-xs text-gray-600 mr-2">
                    {pendingAssignments.length > 0 && (
                      <span className="px-1.5 py-0.5 bg-yellow-100 text-yellow-700 rounded border border-yellow-300">
                        {pendingAssignments.length} assign.
                      </span>
                    )}
                    {pendingModifications.length > 0 && (
                      <span className="px-1.5 py-0.5 bg-yellow-100 text-yellow-700 rounded border border-yellow-300">
                        {pendingModifications.length} modif.
                      </span>
                    )}
                    {pendingAddresses.length > 0 && (
                      <span
                        className="px-1.5 py-0.5 bg-yellow-100 text-yellow-700 rounded border border-yellow-300"
                        title={[
                          addressChanges.created.length ? `${addressChanges.created.length} ajoutée(s)` : null,
                          addressChanges.updated.length ? `${addressChanges.updated.length} modifiée(s)` : null,
                          addressChanges.deleted.length ? `${addressChanges.deleted.length} supprimée(s)` : null,
                        ].filter(Boolean).join(' · ')}
                      >
                        {pendingAddresses.length} adr.
                      </span>
                    )}
                  </div>
                  {canApprove && (
                    <button
                      onClick={handleApprove}
                      disabled={approving}
                      className="px-3 py-2 bg-green-600 hover:bg-green-700 disabled:bg-green-400 text-white rounded-lg flex items-center gap-2 text-sm font-medium transition-colors"
                      title="Approuver les assignations"
                    >
                      <FontAwesomeIcon icon={approving ? faSpinner : faCheckCircle} className={approving ? 'animate-spin' : ''} />
                      <span className="hidden sm:inline">{approving ? 'Approbation...' : 'Approuver'}</span>
                    </button>
                  )}

                  {canReject && (
                    <button 
                      onClick={handleReject}
                      disabled={rejecting}
                      className="px-3 py-2 bg-red-600 hover:bg-red-700 disabled:bg-red-400 text-white rounded-lg flex items-center gap-2 text-sm font-medium transition-colors"
                      title="Rejeter les assignations"
                    >
                      <FontAwesomeIcon icon={rejecting ? faSpinner : faExclamationTriangle} className={rejecting ? 'animate-spin' : ''} />
                      <span className="hidden sm:inline">{rejecting ? 'Rejet...' : 'Rejeter'}</span>
                    </button>
                  )}
                </div>
              )}

              {/* Mobile: Sidebar toggles */}
              <div className="flex lg:hidden gap-1 ml-auto">
                <button
                  onClick={() => setMobileSidebarOpen(mobileSidebarOpen === 'dimensions' ? null : 'dimensions')}
                  className={`p-2 rounded-lg transition-colors ${
                    mobileSidebarOpen === 'dimensions' 
                      ? 'bg-red-600 text-white' 
                      : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                  }`}
                  title="Dimensions"
                >
                  <FontAwesomeIcon icon={faBars} />
                </button>
                <button
                  onClick={() => setMobileSidebarOpen(mobileSidebarOpen === 'summary' ? null : 'summary')}
                  className={`p-2 rounded-lg transition-colors ${
                    mobileSidebarOpen === 'summary' 
                      ? 'bg-red-600 text-white' 
                      : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                  }`}
                  title="Résumé"
                >
                  <FontAwesomeIcon icon={faInfoCircle} />
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Warnings Banner */}
      {warnings.length > 0 && (
        <div className="flex-shrink-0 bg-orange-50 border-b border-orange-200 px-4 py-2">
          <div className="flex flex-wrap gap-2">
            {warnings.map((warning, idx) => (
              <div 
                key={idx} 
                className={`flex items-center gap-2 text-sm px-3 py-1 rounded-full ${
                  warning.type === 'error' 
                    ? 'bg-red-100 text-red-700' 
                    : 'bg-orange-100 text-orange-700'
                }`}
              >
                <FontAwesomeIcon icon={warning.type === 'error' ? faExclamationTriangle : faInfoCircle} className="text-xs" />
                <span>{warning.message}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Quota Helper Panel */}
      {dimensions && dimensions.length > 0 && (
        <QuotaHelperPanel dimensions={dimensions} cells={cells} projectStatus={project?.status} assignmentStatus={project?.assignment_status} userRole={user?.role} pendingModifications={pendingModifications} />
      )}

      {/* Context Chips */}
      {Object.keys(contextFilters).length > 0 && (
        <div className="flex-shrink-0">
          <ContextChips
            filters={contextFilters}
            dimensions={dimensions}
            onFilterRemove={handleContextFilterChange}
            onClearAll={clearContextFilters}
          />
        </div>
      )}

      {/* Main Layout */}
      <div className="flex-1 flex flex-col lg:flex-row overflow-hidden relative min-h-0">
        {/* Mobile Overlay */}
        {mobileSidebarOpen && (
          <div 
            className="lg:hidden fixed inset-0 bg-black/50 z-40"
            onClick={() => setMobileSidebarOpen(null)}
          />
        )}

        {/* Left Sidebar - Dimensions */}
        <div className={`
          hidden lg:flex flex-col flex-shrink-0 bg-white border-r border-gray-200 h-full
          transition-all duration-300 ease-in-out overflow-hidden
          ${showDimensionBuilder ? 'w-72 xl:w-80' : 'w-0'}
        `}>
          {showDimensionBuilder && (
            <>
              <div className="flex-shrink-0 flex items-center justify-between p-4 border-b border-gray-200 bg-gray-50 min-w-[288px] xl:min-w-[320px]">
                <h2 className="font-semibold text-gray-800">Dimensions</h2>
                <button
                  onClick={() => setShowDimensionBuilder(false)}
                  className="p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-lg"
                  title="Fermer"
                >
                  <FontAwesomeIcon icon={faChevronLeft} />
                </button>
              </div>
              <div className="flex-1 overflow-y-auto min-h-0 min-w-[288px] xl:min-w-[320px] quota-sidebar-scroll">
                <DimensionBuilder
                  quotaProjectId={projectId}
                  dimensions={dimensions}
                  cells={cells}
                  overallTarget={project?.overall_target}
                  pendingModifications={pendingModifications}
                  isLocked={project?.status === 'locked' || project?.status === 'finished' || isReadOnly}
                  onDimensionAdded={handleDimensionAdded}
                  onDimensionUpdated={handleDimensionUpdated}
                  onDimensionDeleted={handleDimensionDeleted}
                />
              </div>
            </>
          )}
        </div>

        {/* Left Sidebar Toggle Button (when closed) */}
        {!showDimensionBuilder && (
          <button
            onClick={() => setShowDimensionBuilder(true)}
            className="hidden lg:flex flex-shrink-0 w-10 h-full items-center justify-center bg-white border-r border-gray-200 text-gray-500 hover:text-gray-700 hover:bg-gray-50 transition-colors"
            title="Ouvrir Dimensions"
          >
            <FontAwesomeIcon icon={faChevronRight} />
          </button>
        )}

        {/* Mobile Left Sidebar */}
        <div className={`
          lg:hidden fixed left-0 top-0 h-full w-80 bg-white border-r border-gray-200 z-50
          transition-transform duration-300 ease-in-out flex flex-col
          ${mobileSidebarOpen === 'dimensions' ? 'translate-x-0' : '-translate-x-full'}
        `}>
          <div className="flex-shrink-0 flex items-center justify-between p-4 border-b border-gray-200 bg-gray-50">
            <h2 className="font-semibold text-gray-800">Dimensions</h2>
            <button
              onClick={() => setMobileSidebarOpen(null)}
              className="p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-lg"
            >
              <FontAwesomeIcon icon={faTimes} />
            </button>
          </div>
          <div className="flex-1 overflow-y-auto min-h-0 quota-sidebar-scroll">
            <DimensionBuilder
              quotaProjectId={projectId}
              dimensions={dimensions}
              cells={cells}
              overallTarget={project?.overall_target}
              pendingModifications={pendingModifications}
              isLocked={project?.status === 'locked' || project?.status === 'finished'}
              onDimensionAdded={handleDimensionAdded}
              onDimensionUpdated={handleDimensionUpdated}
              onDimensionDeleted={handleDimensionDeleted}
            />
          </div>
        </div>

        {/* Center - Quota Matrix */}
        <div className="flex-1 overflow-auto bg-gray-100 px-4 pb-4 quota-main-content min-h-0 quota-main-scroll">
          <QuotaMatrix
            key={`quota-matrix-${pendingAssignments.length}-${pendingAssignments.map(pa => pa.id).join(',')}-mods-${pendingModifications.length}`}
            quotaProjectId={projectId}
            dimensions={dimensions}
            cells={cells}
            contextFilters={contextFilters}
            calculator={calculator}
            overallTarget={project?.overall_target}
            isLocked={project?.status === 'locked' || project?.status === 'finished' || project?.status === 'archived' || isReadOnly}
            assignmentStatus={project?.assignment_status}
            projectStatus={project?.status}
            pendingAssignments={pendingAssignments}
            pendingModifications={pendingModifications}
            onCellSelect={(cell) => {
              setSelectedCell(cell);
              setShowCellDetail(true);
            }}
            onCellAdded={handleCellAdded}
            onCellUpdated={handleCellUpdated}
            onCellDeleted={handleCellDeleted}
            onCancelPendingDeletion={handleCancelPendingDeletion}
            onBulkAssignSupervisors={canBulkAssignSupervisors ? () => setShowBulkAssignModal(true) : null}
            onImportComplete={fetchProjectData}
          />
        </div>

        {/* Right Sidebar Toggle Button (when closed) */}
        {!showSummary && (
          <button
            onClick={() => setShowSummary(true)}
            className="hidden lg:flex flex-shrink-0 w-10 h-full items-center justify-center bg-white border-l border-gray-200 text-gray-500 hover:text-gray-700 hover:bg-gray-50 transition-colors"
            title="Ouvrir Résumé"
          >
            <FontAwesomeIcon icon={faChevronLeft} />
          </button>
        )}

        {/* Right Sidebar - Summary */}
        <div className={`
          hidden lg:flex flex-col flex-shrink-0 bg-white border-l border-gray-200 h-full
          transition-all duration-300 ease-in-out overflow-hidden
          ${showSummary ? 'w-72 xl:w-80' : 'w-0'}
        `}>
          {showSummary && (
            <>
              <div className="flex-shrink-0 flex items-center justify-between p-4 border-b border-gray-200 bg-gray-50 min-w-[288px] xl:min-w-[320px]">
                <h2 className="font-semibold text-gray-800">Résumé</h2>
                <button
                  onClick={() => setShowSummary(false)}
                  className="p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-lg"
                  title="Fermer"
                >
                  <FontAwesomeIcon icon={faChevronRight} />
                </button>
              </div>
              <div className="flex-1 overflow-y-auto min-h-0 min-w-[288px] xl:min-w-[320px] quota-sidebar-scroll">
                <QuotaSummary
                  project={project}
                  calculator={calculator}
                  onFilterChange={handleContextFilterChange}
                />
              </div>
            </>
          )}
        </div>

        {/* Mobile Right Sidebar */}
        <div className={`
          lg:hidden fixed right-0 top-0 h-full w-80 bg-white border-l border-gray-200 z-50
          transition-transform duration-300 ease-in-out flex flex-col
          ${mobileSidebarOpen === 'summary' ? 'translate-x-0' : 'translate-x-full'}
        `}>
          <div className="flex-shrink-0 flex items-center justify-between p-4 border-b border-gray-200 bg-gray-50">
            <h2 className="font-semibold text-gray-800">Résumé</h2>
            <button
              onClick={() => setMobileSidebarOpen(null)}
              className="p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-lg"
            >
              <FontAwesomeIcon icon={faTimes} />
            </button>
          </div>
          <div className="flex-1 overflow-y-auto min-h-0 quota-sidebar-scroll">
            <QuotaSummary
              project={project}
              calculator={calculator}
              onFilterChange={handleContextFilterChange}
            />
          </div>
        </div>
      </div>

      {/* Bottom Panel - Cell Detail */}
      {showCellDetail && selectedCell && (
        <CellDetailPanel
          cell={selectedCell}
          dimensions={dimensions}
          isLocked={project?.status === 'locked' || project?.status === 'finished' || isReadOnly}
          pendingAssignments={pendingAssignments}
          pendingModifications={pendingModifications}
          assignmentStatus={project?.assignment_status}
          onClose={() => {
            setShowCellDetail(false);
            setSelectedCell(null);
          }}
          onUpdate={handleCellUpdated}
          onDelete={handleCellDeleted}
          onCancelPendingDeletion={handleCancelPendingDeletion}
        />
      )}

      <AlertModal
        show={alertModal.show}
        title={alertModal.title}
        message={alertModal.message}
        type={alertModal.type}
        onClose={() => setAlertModal({ show: false, title: '', message: '', type: 'info' })}
      />

      <ConfirmationModal
        show={showValidateConfirm}
        title="Démarrer le projet"
        message="Êtes-vous sûr de vouloir démarrer le projet et le verrouiller ? Une fois verrouillé, il ne pourra plus être modifié."
        confirmText="Ok"
        cancelText="Annuler"
        variant="warning"
        onConfirm={confirmValidate}
        onCancel={() => setShowValidateConfirm(false)}
      />

      <ConfirmationModal
        show={showFinishConfirm}
        title="Terminer le quota"
        message="Êtes-vous sûr de vouloir marquer ce quota comme terminé ? Cela indique que le travail de terrain est complété."
        confirmText="Terminer"
        cancelText="Annuler"
        variant="success"
        onConfirm={confirmFinish}
        onCancel={() => setShowFinishConfirm(false)}
      />

      <ConfirmationModal
        show={showUnlockConfirm}
        title="Déverrouiller le projet"
        message={
          <>
            Cette action va déverrouiller le projet et le remettre à son statut précédent.
            <br /><br />
            <strong>Attention :</strong> Cette action va perturber les activités de terrain. Les quotas ne seront plus visibles pour les prestataires et les enquêteurs. Il est fortement conseillé de les avertir avant de procéder.
          </>
        }
        confirmText="Confirmer le déverrouillage"
        cancelText="Annuler"
        variant="warning"
        onConfirm={confirmUnlock}
        onCancel={() => setShowUnlockConfirm(false)}
      />
      
      {/* Summary of the batch the chef_terrain_superieur is about to approve */}
      <ConfirmationModal
        show={showApproveConfirm}
        title="Valider les modifications"
        message={
          <div className="space-y-3 text-sm">
            {totalPendingChanges === 0 ? (
              <p>Aucune modification en attente pour ce projet.</p>
            ) : (
              <>
                <p>Voici le récapitulatif des modifications soumises par le chef de terrain :</p>

                {pendingAssignments.length > 0 && (
                  <div>
                    <div className="font-semibold text-gray-800">Assignations : {pendingAssignments.length}</div>
                  </div>
                )}

                {pendingModifications.length > 0 && (
                  <div>
                    <div className="font-semibold text-gray-800">Cellules : {pendingModifications.length}</div>
                    <ul className="list-disc list-inside text-gray-600">
                      {cellChanges.created.length > 0 && <li>{cellChanges.created.length} création(s)</li>}
                      {cellChanges.deleted.length > 0 && <li>{cellChanges.deleted.length} suppression(s)</li>}
                      {cellChanges.targets.length > 0 && <li>{cellChanges.targets.length} changement(s) d'objectif</li>}
                      {cellChanges.others.length > 0 && <li>{cellChanges.others.length} autre(s) modification(s)</li>}
                    </ul>
                  </div>
                )}

                {pendingAddresses.length > 0 && (
                  <div>
                    <div className="font-semibold text-gray-800">Adresses : {pendingAddresses.length}</div>
                    <ul className="list-disc list-inside text-gray-600">
                      {addressChanges.created.length > 0 && <li>{addressChanges.created.length} ajoutée(s)</li>}
                      {addressChanges.updated.length > 0 && <li>{addressChanges.updated.length} modifiée(s)</li>}
                      {addressChanges.deleted.length > 0 && <li>{addressChanges.deleted.length} supprimée(s)</li>}
                    </ul>
                    <div className="mt-2 max-h-40 overflow-y-auto border border-gray-200 rounded divide-y divide-gray-100">
                      {pendingAddresses.map(pa => (
                        <div key={pa.id} className="flex items-start gap-2 px-2 py-1">
                          <span className={`shrink-0 mt-0.5 px-1.5 py-0.5 rounded text-[10px] font-semibold uppercase ${
                            pa.operation === 'delete'
                              ? 'bg-red-100 text-red-700'
                              : pa.operation === 'create'
                                ? 'bg-green-100 text-green-700'
                                : 'bg-blue-100 text-blue-700'
                          }`}>
                            {pa.operation === 'delete' ? 'Suppr.' : pa.operation === 'create' ? 'Ajout' : 'Modif.'}
                          </span>
                          <span className="text-gray-700 break-words">{addressLabel(pa)}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                <p className="text-gray-600">
                  Confirmez-vous la validation de ces modifications ? Les suppressions d'adresses sont définitives.
                </p>
              </>
            )}
          </div>
        }
        confirmText={approving ? 'Validation...' : 'Valider'}
        cancelText="Annuler"
        variant="success"
        confirmDisabled={approving}
        onConfirm={confirmApprove}
        onCancel={() => setShowApproveConfirm(false)}
      />

      <RejectionModal
        show={showRejectionModal}
        onClose={() => setShowRejectionModal(false)}
        onConfirm={confirmReject}
        isSubmitting={rejecting}
      />
      
      {/* Bulk Assign Supervisors Modal */}
      {showBulkAssignModal && (
        <AssignSupervisorModal
          quotaProjectId={projectId}
          cells={cells}
          pendingAssignments={pendingAssignments}
          pendingModifications={pendingModifications}
          onClose={() => {
            setShowBulkAssignModal(false);
          }}
          onSuccess={(updatedData) => {
            // Handle bulk assignment response
            if (updatedData?._pendingAssignments) {
              // Update pending assignments
              setPendingAssignments(prev => {
                const newPending = [...prev];
                updatedData._pendingAssignments.forEach(pa => {
                  const existingIndex = newPending.findIndex(p => Number(p.id) === Number(pa.id));
                  if (existingIndex >= 0) {
                    newPending[existingIndex] = pa;
                  } else {
                    newPending.push(pa);
                  }
                });
                return newPending;
              });
            }
            
            // Refresh project data to get updated cells
            fetchProjectData();
            
            // Close modal
            setShowBulkAssignModal(false);
          }}
        />
      )}
    </div>
  );
};

export default QuotaBuilder;
